const Blockly = require('blockly');
const logging = require('@sstdev/lib_logging');
const dataAccessMethods = require('../../../dataAccessMethods');
const baseMdcBlockGroup = require('../_BaseMdcBlockGroup');
const setupAddCodeProperties = require('../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../helpers/convertToBoolean');
const arrayFromCommaSeparatedValues = require('../../../helpers/arrayFromCommaSeparatedValues');
const hNodeTypes = require('./types');

const TOOLTIPS = {
    MultiUserPickerDropDownSearch:
        'Specialized MultiPickerDropdownSearch for identity:user. Can be used inside a Nested Detail with just `modifiedBy`, or by hardcoding the full path like: `meta.modifiedBy`'
};

class SearchElement extends baseMdcBlockGroup {
    /**
     * @param {{[key: string]: any}} code
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        if (hNodeTypes[hNodeType]?.convertCustomFieldsToCode) {
            const resultCode = hNodeTypes[hNodeType].convertCustomFieldsToCode(code, blocklyBlock);
            if (typeof resultCode === 'string') {
                logging.error(
                    `${hNodeType}.convertCustomFieldsToCode should not return a string. It should return a JSON object instead.`
                );
                return resultCode;
            }
            return JSON.stringify(resultCode);
        }

        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

        // These are most common, but we overwrite/clear for specific HNodes
        addCodeProperties(['namespace', 'relation', 'propertyName', { key: 'forAction', handler: () => 'filter' }]);

        // For dropdown identify namespace and relation as foriegn relations
        if (['DropDownSearch', 'MultiPickerDropDownSearch'].includes(hNodeType)) {
            addCodeProperties([
                { key: 'foreignNamespace', value: 'namespace' },
                { key: 'foreignRelation', value: 'relation' }
            ]);
            code.namespace = undefined;
            code.relation = undefined;
        }

        if (hNodeType === 'DropDownSearch') {
            addCodeProperties([
                { key: 'choices', handler: arrayFromCommaSeparatedValues },
                { key: 'propertyOverride', handler: value => value || undefined }
            ]);
        }

        // Song and dance to figure out the sequence based on order in the workspace.
        const parent = blocklyBlock.getSurroundParent();
        const parentChildren = parent.getDescendants(undefined);
        addCodeProperties([
            {
                key: 'sequence',
                handler: () =>
                    parentChildren.findIndex(function (value) {
                        return value.id === blocklyBlock.id;
                    }) - 1 // subtract 1 because includes parent with getDescendants
            }
        ]);

        if (hNodeType === 'MultiUserPickerDropDownSearch') {
            addCodeProperties(['propertyOverride']);
        }

        const filterable = ['MultiPickerDropDownSearch', 'MultiUserPickerDropDownSearch'].includes(hNodeType);
        if (filterable) addCodeProperties([{ key: 'filterable', handler: convertToBoolean }]);

        const hasTooltip = hNodeType.includes('DropDown');
        if (hasTooltip) addCodeProperties(['tooltip']);

        const hasAutoFillAndHide = hNodeType.includes('DropDownSearch');
        if (hasAutoFillAndHide) addCodeProperties([{ key: 'autoHide', handler: convertToBoolean }]);

        const hasDisplayProperties = hNodeType.includes('DropDown');
        if (hasDisplayProperties) {
            addCodeProperties([
                { key: 'dropdownDisplayProperties', handler: arrayFromCommaSeparatedValues },
                { key: 'inputDisplayProperties', handler: arrayFromCommaSeparatedValues }
            ]);
        }

        if (hNodeType === 'ValueSearch') {
            // @ts-ignore
            code.filters = JSON.parse(`[${Blockly.JavaScript.statementToCode(blocklyBlock, 'filters')}]`);
            // `hidden` keeps the element mounted (so a loaded saved filter still applies) but out of the
            // visible Filter By pane — used by the Serialized Each meta.import filter (ValueSearch honors it).
            addCodeProperties([{ key: 'hidden', handler: convertToBoolean }]);
        }
        if (hNodeType.includes('DropDown')) {
            addCodeProperties(['placeholder', 'emptyHint']);
        }

        if (['ValueSearch', 'DropDownSearch'].includes(hNodeType)) {
            addCodeProperties([
                { key: 'displayScanButton', handler: convertToBoolean },
                { key: 'displayRfidPowerButton', handler: convertToBoolean },
                { key: 'scanRfid', handler: convertToBoolean },
                { key: 'scanBarcode', handler: convertToBoolean }
            ]);
        }
        return JSON.stringify(code);
    }

    /**
     * @param {unknown} color
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addToBlocksCustom(color, blocklyBlock, hNodeType, mdcBlock, topInput) {
        if (hNodeTypes[hNodeType]) {
            hNodeTypes[hNodeType].addBlockSpecificFields(color, blocklyBlock, mdcBlock, topInput);
            return;
        }

        if (TOOLTIPS[hNodeType]) {
            blocklyBlock.setTooltip(TOOLTIPS[hNodeType]);
        }

        const blockId = blocklyBlock.id;
        const workspace = blocklyBlock.workspace;

        if (['DropDownSearch', 'MultiPickerDropDownSearch'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Namespace:')
                .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces(true)), 'namespace')
                .appendField('  Relation:')
                .appendField(
                    new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                    'relation'
                );
        }
        if (hNodeType === 'DropDownSearch') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Fixed choices (optional):')
                .appendField(new Blockly.FieldTextInput(''), 'choices')
                .appendField('  Filter property:')
                .appendField(new Blockly.FieldTextInput(''), 'propertyOverride');
        }
        if (hNodeType === 'MultiUserPickerDropDownSearch') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Property Override:')
                .appendField(new Blockly.FieldTextInput('modifiedBy'), 'propertyOverride');
        }

        const filterable = ['MultiPickerDropDownSearch', 'MultiUserPickerDropDownSearch'].includes(hNodeType);
        if (filterable) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Filterable:')
                .appendField(new Blockly.FieldCheckbox('false'), 'filterable');
        }

        const hasAutoFillAndHide = hNodeType.includes('DropDownSearch');
        if (hasAutoFillAndHide) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Auto Hide:')
                .appendField(new Blockly.FieldCheckbox('false'), 'autoHide');
        }

        const hasTooltip = hNodeType.includes('DropDown');
        if (hasTooltip) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Tooltip:')
                .appendField(new Blockly.FieldTextInput(''), 'tooltip');
        }

        const hasDisplayProperties = hNodeType.includes('DropDown');
        if (hasDisplayProperties) {
            blocklyBlock
                .appendDummyInput()
                .appendField(new Blockly.FieldTextInput('title'), 'propertyName')
                .setVisible(false);
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Properties to include in dropdown:')
                .appendField(new Blockly.FieldTextInput('title'), 'dropdownDisplayProperties');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Properties to include in input:')
                .appendField(new Blockly.FieldTextInput('title'), 'inputDisplayProperties');
        }

        const hasContentFilters = hNodeType.includes('DropDownSearch');
        if (hasContentFilters) {
            blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Dropdown Content Filters:');
        }


        if (hNodeType === 'ValueSearch') {
            blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Hidden (mounted, but not shown in Filter By):')
                .appendField(new Blockly.FieldCheckbox('false'), 'hidden');
        }

        if (hNodeType.includes('DropDown')) {
            blocklyBlock
                .appendDummyInput()
                .appendField('  Placeholder')
                .appendField(new Blockly.FieldTextInput(), 'placeholder')
                .appendField('  Empty Hint')
                .appendField(new Blockly.FieldTextInput('No records found'), 'emptyHint');
        }

        if (['ValueSearch', 'DropDownSearch'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput('scanningOptions')
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Scan Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayScanButton')
                .appendField('  Display RFID Power Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayRfidPowerButton')
                .appendField('  Scan RFID')
                .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
                .appendField('  Scan Barcode')
                .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
        }
    }

    customizeToolbox(section) {
        section.contents.forEach(block => {
            // mutate the `block` as shown in the toolbox
            // but only if something is actually implemented for that block type (hNodeType)
            const [, type] = block.type.split('_');
            hNodeTypes[type]?.customizeToolbox?.(block);

            if (block.type.includes('DropDown')) {
                block.inputs = {
                    filters: {
                        block: {
                            type: 'filter_OrderBy',
                            fields: { propertyName: 'title' }
                        }
                    }
                };
            }
            if (block.type === 'searchElement_ValueSearch') {
                block.inputs = {
                    filters: {
                        block: {
                            type: 'filter_PropertiesToSearch',
                            fields: { propertiesToSearch: 'assetNo,serialNo' }
                        }
                    }
                };
            }
        });
    }
}

module.exports = SearchElement;
